import { applicationContext, ApplicationModule, foundationContext, Information } from '@philips-internal/oswald-ui-foundation';
import { BaseModalProps, DeckAPI, FetchAPI, LinkAPI, MapperAPI, ModalService, NotificationCenterAPI, OptionAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnClick, OnMount, OnOpen, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableFetchEvent, ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AuthenticationService, BrokerService, HttpService, JWTToken, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Type } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { Inject } from '@odinjs/odin';
import moment from 'moment/moment.js';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import AllParameter from '../../interfaces/dtos/allparameter-dto';
import ConfigurationService from '../../services/configuration-service';
import PatientDataService from '../../services/patient-data-service';
import BaseController from '../BaseController';
import { APPOINTMENT_TYPE } from '../../constants/appointment-type';
import UserContext from '../../utils/user-context';
import { PessoaFisicaDependente } from '../../interfaces/pessoa-fisica-dependente';
import { UserInformationWithFoto } from '../../interfaces/pessoa-fisica-with-foto';
import TokenConstants from '../../constants/TokenConstants';
import RescheduleContext from '../../utils/reschedule-utils';
import { TasyRescheduleParameters } from '../../interfaces/tasy-parameters';
import { ApplicationModules, ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { UserDependent, UserPrincipal } from '../../app/types';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { cast, clone } from '../../utils/cast-helpers';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import { applicationModuleRegistry } from '@philips-internal/oswald-ui-foundation';

import './patient-root.css';
import { FragmentNames, PatientRootAliases } from '../../codegen';
import ApplicationTermService from '../../services/application-term-service';
import TeleconsultattionApi from '@philips-emr/patient-components/src/components/teleconsultation/teleconsultation-api';
import { BS_CHANGE_USER } from '../../constants/application-constants';
import { ScheduleParameters } from 'src/interfaces/schedule-parameters';

interface Notification {
  appointmentType: string;
  cancelDate: string;
  cdMedico: string;
  dtAtualizacao: string;
  dtAtualizacaoNrec: string;
  duration: string;
  endDate: string;
  examName: string;
  localeValue: string;
  notificationStatus: string;
  notificationType: string;
  nrAuxKey: number;
  nrPrescricao: number;
  nrSeqExam: string;
  nrSequencia: number;
  nrUnreadOrder: number;
  releaseDateServer: string;
  resultDescription: string;
  revised: string;
  scheduleStatus: string;
  sequence: number;
  statusCode: string;
}

interface ParametersItem {
  appointmentType?: string;
  cdMedico?: string;
  dtProcedimento?: string;
  examType?: string,
  filterExamStatus?: string;
  filterExamType?: string,
  filterFromDate?: string;
  nrPrescricao?: number;
  nrSeq?: number;
  nrSequencia?: number;
  patientId?: string | number;
}

interface NavigationParameters {
  applicationBarModuleIdParent: ApplicationModulesIds;
  parameters: ParametersItem;
  url: string;
}

interface NavigationParameters {
  parameters: ParametersItem;
  url: string;
}

interface NotificationCount {
  counts: number;
  personCode: string;
}

export interface UrlTelemedicine {
  patientUrl: string
}

enum NOTIFICATION_CLICK_TYPE {
  NOTIFICATION = 'NotificationClick',
  MARK_AS_READ = 'MarkAsRead',
  MARK_AS_UNREAD = 'MarkAsUnread',
  MARK_ALL_AS_READ = 'MarkAllAsRead',
  DELETE = 'Delete'
}

/**
 * These values are based on the returned data for notifications.
 * It uses notificationType(AC,AP,Q) and appointmentType (consultation=3,4; services=5; surgery=1; exam=2)
 * and for the appointment identification uses nrPrescricao
 * -1 identifier is used to maintain the standard, it does not used in the algorythm
 */
const APPOINTMENT_NOTIFICATION_TYPE = {
  AC: {
    '3': APPOINTMENT_TYPE.CONSULTATIONS,
    '4': APPOINTMENT_TYPE.CONSULTATIONS,
    '5': APPOINTMENT_TYPE.SERVICES
  },
  AP: {
    '1': APPOINTMENT_TYPE.SURGERIES,
    '2': APPOINTMENT_TYPE.EXAMS
  },
  CH: {
    '6': APPOINTMENT_TYPE.CHECKUP
  },
  Q: {
    '-1': APPOINTMENT_TYPE.CHEMOTHERAPIES
  }
};

/**
 * This value is based on the returned data for notifications.
 * For notification of profile update requisition. Any notification of this type redirects to profile page
 */
const PROFILE_UPDATE_REQUIRED_NOTIFICATION_TYPE = {
  RC: APPLICATION_ROUTES.PROFILE
};

/**
 * These values are based on the returned data for notifications.
 * It uses notificationType(P=patology,R=non-lab,L=lab) and these values are translated to examType(P=patology,N=non-lab,L=lab)
 * and for the identifiers it uses nrPrescricao and nrAuxKey(this will contain the nrSeq of the exam result)
 */
const EXAM_NOTIFICATION_TYPE = {
  L: { 'L': APPLICATION_ROUTES.EXAM_LAB_DETAILS },// lab
  P: { 'P': APPLICATION_ROUTES.EXAM_DETAILS },// patology
  R: { 'N': APPLICATION_ROUTES.EXAM_DETAILS },// non-lab( in database is R in application the value is N)
}

@Controller({ fragment: FragmentNames.PATIENT_ROOT })
export default class PatientRoot extends BaseController {

  private holderPatientId = 0;
  private patientId = 0;
  private dependentList: PessoaFisicaDependente[] = [];
  private areNotificationsAlreadySetToViewed = false;
  private parameters!: AllParameter;

  mapperUserProfile!: MapperAPI;

  @Inject('mapper-user-profile-2')
    mapperUserDependents!: MapperAPI;

  @Inject('panel-dependents')
    panelDependents!: PanelAPI;

  @Inject(PatientRootAliases.OPTION_PROFILE)
    optionProfile!: OptionAPI;

  @Inject('fetch-user-profile')
    fechUserProfile!: FetchAPI;

  @Inject('notification-center')
    notificationCenter!: NotificationCenterAPI;

  @Inject('fetch-notification')
    fetchNotification!: FetchAPI;

  @Inject(PatientRootAliases.FETCH_USER_PROFILE_HOLDER)
    fetchDropdownUserPrincipal!: FetchAPI;

  private reloadDashboard(): void {
    const event = new CustomEvent('reloadDashboard', { detail: { patientId: this.patientId } });
    dispatchEvent(event);
  }

  private updateCurrentUserInMapper(patientId: number, dependentList: PessoaFisicaDependente[]): void {
    dependentList
      .filter(e => e.cdPessoaDependente === patientId.toString())
      .forEach(selectedDependent => {
        const {
          cdPessoaDependente: cdPessoaFisica,
          nmPessoaDependente: nmPessoaFisica,
          imPessoaFisicaBase64,
          dsGrauParentesco
        } = selectedDependent;

        if (this.mapperUserProfile) {
          this.mapperUserProfile.setData({
            cdPessoaFisica,
            dsGrauParentesco,
            imPessoaFisicaBase64,
            nmPessoaFisica,
          });
        }
      });
  }

  private loadDependentsList(): void {
    HttpService.get<PessoaFisicaDependente[]>(
      APPLICATION_ENDPOINTS.PROFILE_DEPENDENTS_LIST).then(response => {
      const dependents = response.body as PessoaFisicaDependente[]
      this.dependentList = dependents;
      UserContext.userDependents = dependents;
    });
  }

  private showDependents(): void {
    const showProfileLink = this.isEnableProfileModule(this.patientId);
    this.optionProfile.setVisible(showProfileLink);
    this.panelDependents.setVisible(this.dependentList.length !== 0);
  }

  private isEnableProfileModule(patientId: number): boolean {
    const { userPrincipal } = UserContext;
    return (patientId !== 0 && patientId != null && userPrincipal?.cdPessoaFisica === String(patientId));
  }

  private updatePatientDataInAvatarByMapper(mapperApi: MapperAPI): void {
    if (!mapperApi) {
      return;
    }

    mapperApi.addCustomMapping('image', data => {
      const userInformation = data as unknown as UserInformationWithFoto;
      return userInformation.imPessoaFisicaBase64 ? userInformation.imPessoaFisicaBase64 : '/static/assets/icons/avatar-user-gray.svg';
    });

    mapperApi.addCustomMapping('subtitle', (data): Value => {
      if (!Array.isArray(data)) {
        return data?.dsGrauParentesco || MessageService.translate('patient.account.holder');
      }

      return '-';
    });
  }

  private switchProfile(patientId: number): void {
    this.context.set({
      patientId
    });

    this.patientId = patientId;
    UserContext.patientId = this.patientId;

    if (this.fetchNotification) {
      this.fetchNotification.setParameters({ ...this.fetchNotification.getParameters(), patientId });
      this.fetchNotification.forceFetch();
      this.areNotificationsAlreadySetToViewed = false;
      this.updateNotificationCount();
    }

    applicationContext.clearNavigationStack();
    if (applicationContext.getCurrentModule()?.id !== ApplicationModulesIds.DASHBOARD) {
      applicationContext.openModule(ApplicationModulesIds.DASHBOARD);
    }

    this.notifyChangedUserToTaskComponent(patientId);
    this.removeOrIncluideProfileModule(patientId);
    this.reloadDashboard();

    if (Number(patientId || 0) === Number(this.holderPatientId || 0)) {
      return;
    }

    this.updateCurrentUserInMapper(patientId, this.dependentList);
  }

  /**
   * This action does not prohibit the user from accessing the profile via the url.
   * The limitation is for dependents only.
   */
  private removeOrIncluideProfileModule(patientId: number): void {
    const isEnableProfileModule = this.isEnableProfileModule(patientId);

    const appContext = getFoundationDataContext();
    const applicationInformationDataOriginal = appContext.get('applicationInformationData') as Information;
    const applicationInformationDataTemp = clone<Information, Information>(applicationInformationDataOriginal);
    const currentModulesAvailable = applicationInformationDataTemp.module.available;

    const userInformation = foundationContext.getUser();
    if (userInformation) {
      applicationInformationDataTemp.user.id = userInformation.id;
      applicationInformationDataTemp.user.name = userInformation.name;
      applicationInformationDataTemp.user.username = userInformation.username;
      applicationInformationDataTemp.user.image = userInformation.image;
    }

    if (isEnableProfileModule) {
      const profileModule = foundationContext.getModule(ApplicationModulesIds.PROFILE);
      if (!profileModule) {
        foundationContext.clearInformation();
        foundationContext.setInformationOnce(applicationInformationDataTemp);
      }
      return;
    }

    applicationContext.closeModule(ApplicationModulesIds.PROFILE);
    foundationContext.clearInformation();
    const newModulesAvailable = currentModulesAvailable.filter(module => module.id !== ApplicationModulesIds.PROFILE);
    applicationInformationDataTemp.module.available = newModulesAvailable;
    foundationContext.setInformationOnce(applicationInformationDataTemp);
  }

  private pushToUrl(url: NavigationParameters, parameters: ParametersItem): void {
    const options = {
      appointmentType: parameters.appointmentType || '',
      cdMedico: parameters.cdMedico || '',
      dtProcedimento: parameters.dtProcedimento || '',
      examType: parameters.examType || '',
      filterExamStatus: parameters.filterExamStatus || '',
      filterExamType: parameters.filterExamType || '',
      filterFromDate: parameters.filterFromDate || '',
      nrPrescricao: parameters.nrPrescricao || '',
      nrSeq: parameters.nrSeq || '',
      nrSequencia: parameters.nrSequencia || '',
      patientId: parameters.patientId || '',
    }

    const targetRoute = url.url;
    const moduleId = this.getRouteModuleId(targetRoute);

    const isExamRoute = moduleId === ApplicationModulesIds.EXAMS
    const pathParameters = isExamRoute ? {} : options;
    const queryParameters = isExamRoute ? options : {};

    if (moduleId) {
      applicationContext.openModule(moduleId, {
        at: {
          options: {
            searchParams: queryParameters,
          },
          params: pathParameters,
          path: targetRoute,
        }
      });
    } else {
      applicationContext.redirect(url.url, options);
    }
  }

  private getRouteModuleId(route: string): string | undefined {
    const appModules: ApplicationModule[] = applicationModuleRegistry.getModules();
    const routeModule = appModules.find(module => route.match(module.route));

    return routeModule?.id
  }

  private getRouteDataForNavigation(notification: Notification): NavigationParameters {
    const isAppointment = Object.keys(APPOINTMENT_NOTIFICATION_TYPE)
      .some(k => k === notification.notificationType);
    const isProfileUpdatedRequired = Object.keys(PROFILE_UPDATE_REQUIRED_NOTIFICATION_TYPE)
      .some(k => k === notification.notificationType);
    const isExam = Object.keys(EXAM_NOTIFICATION_TYPE)
      .some(k => k === notification.notificationType);

    if (isAppointment) {
      return this.buildAppointmentsUrlAndParameters(notification);
    }

    if (isExam) {
      return this.buildExamResultsUrlAndParameters(notification);
    }

    if (isProfileUpdatedRequired) {
      return this.buildProfileUpdateRequiredUrlAndParameters();
    }

    throw new Error('The notification type is not supported')
  }

  private buildAppointmentsUrlAndParameters(notification: Notification): NavigationParameters {
    const { nrPrescricao: nrSeq, appointmentType: subNotificationType } = notification;
    const url = APPLICATION_ROUTES.PSEUDONYM_APPOINTMENT_DETAIL;
    const appointmentType = Object.entries(APPOINTMENT_NOTIFICATION_TYPE)
      .map(e => ({ key: e[0], value: e[1] }))
      .filter((e) => e.key === notification.notificationType)
      .map(e => ({
        items: e.value,
        key: (e.key === 'Q' ? Object.keys(APPOINTMENT_NOTIFICATION_TYPE.Q)[0] : subNotificationType)
      }))
      .slice(0, 1)
      .reduce((prev, curr) => `${(curr.items as { [key: string]: APPOINTMENT_TYPE })[curr.key] || prev}`, '');

    const { patientId } = UserContext;

    return {
      applicationBarModuleIdParent: ApplicationModulesIds.APPOINTMENTS,
      parameters: {
        appointmentType,
        nrSeq,
        patientId,
      },
      url,
    };
  }

  private buildExamResultsUrlAndParameters(notification: Notification): NavigationParameters {
    const firstKeyValuePairInConf = Object.entries(EXAM_NOTIFICATION_TYPE)
      .map(e => ({ key: e[0], value: e[1] }))
      .filter(e => e.key === notification.notificationType)
      .slice(0, 1);
    const url = firstKeyValuePairInConf
      .map(e => Object.values(e.value)[0])
      .reduce((prev, curr) => `${curr || prev}`, '');
    const examType = firstKeyValuePairInConf
      .map(e => Object.keys(e.value)[0])
      .reduce((prev, curr) => `${curr || prev}`, '');
    const { nrAuxKey: nrSequencia, nrPrescricao, cdMedico, releaseDateServer } = notification;
    const { patientId } = UserContext;

    const lastMonth = new Date();
    lastMonth.setMonth(new Date().getMonth() - 1);
    const filterFromDate = lastMonth.toISOString();

    const filterExamStatus = 'ALL';
    const filterExamType = 'ALL';
    const dtProcedimento = releaseDateServer.split(' ')[0];

    return {
      applicationBarModuleIdParent: ApplicationModulesIds.EXAMS,
      parameters: {
        cdMedico,
        dtProcedimento,
        examType,
        filterExamStatus,
        filterExamType,
        filterFromDate,
        nrPrescricao,
        nrSequencia,
        patientId,
      },
      url,
    };
  }

  private buildProfileUpdateRequiredUrlAndParameters(): NavigationParameters {
    const url = PROFILE_UPDATE_REQUIRED_NOTIFICATION_TYPE.RC;

    return {
      applicationBarModuleIdParent: ApplicationModulesIds.PROFILE,
      parameters: {},
      url,
    };
  }

  private updateAllNotificationsToViewed(): void {
    const { patientId } = UserContext;

    HttpService.post(
      APPLICATION_ENDPOINTS.NOTIFICATION_UPDATE_ALL_TO_VIEWED, { patientId },
    ).then(() => this.areNotificationsAlreadySetToViewed = true)
  }

  private updateAllNotificationsToRead(): void {
    const { patientId } = UserContext;

    HttpService.post(
      APPLICATION_ENDPOINTS.NOTIFICATION_UPDATE_ALL_TO_READ, { patientId },
    ).then(() => {
      this.notificationCenter.markAllAsRead();
      this.updateNotificationCount();
    });
  }

  private async updateNotificationStatus(id: number, read: boolean): Promise<void> {
    const { patientId } = this;
    const ENDPOINT_READ = APPLICATION_ENDPOINTS.NOTIFICATION_UPDATE_STATUS_READ;
    const ENDPOINT_UNREAD = APPLICATION_ENDPOINTS.NOTIFICATION_UPDATE_STATUS_UNREAD;
    const ENDPOINT = read ? ENDPOINT_UNREAD : ENDPOINT_READ;
    await HttpService.post(ENDPOINT,
      { patientId }, { body: [id] as unknown as Record<string, Value> })
      .then(() => {
        this.notificationCenter.setReadStatus(id, !read);
        this.updateNotificationCount();
      });
  }

  private async handleMarkAsReadOrUnreadClick(event: ClickEvent): Promise<void> {
    const { id, read } = event.value.item;

    await this.updateNotificationStatus(id, read);
  }

  private async updateNotificationCount(): Promise<void> {
    const { patientId, parameters } = this;

    HttpService.get(APPLICATION_ENDPOINTS.NOTIFICATION_COUNT_URL, { patientId, ...parameters }).then(res => {
      const body = res.body as NotificationCount;
      this.notificationCenter.setUnreadNotificationsCount(body.counts);
    })
  }

  private handleDeleteNotificationClick(event: ClickEvent): void {
    const { id } = event.value.item;

    this.deleteNotification(id);
  }

  private deleteNotification(id: number): void {
    const { patientId } = this;

    HttpService.delete(APPLICATION_ENDPOINTS.NOTIFICATION_DELETE,
      { notificationId: id, patientId }, {})
      .then(() => {
        this.notificationCenter.deleteNotification(id);
        this.updateNotificationCount();
      });
  }

  @OnMount({ target: 'panel-dropdown' })
  handlePanelDropdownLoading(event: MountEvent): void {
    const panelAPI = event.target as PanelAPI;
    if (isMobile()) {
      panelAPI.setVisible(false);
    }
  }

  @OnMount(PatientRootAliases.ROOT_LAYOUT)
  onApplicationBarMount(): void {
    ApplicationTermService.hasSignedRequiredConsents()
      .then(() => this.doRouteValidations());

    const token = AuthenticationService.getJWTToken() as JWTToken;
    const patientId: number = token.claims[TokenConstants.PATIENT_ID] as number;

    this.setUserContextData(patientId);

    this.holderPatientId = patientId;
    this.patientId = patientId;

    PatientDataService.getAllParameters()
      .then(parameters => {
        const data = { patientId, ...parameters };
        this.context.set(data);
        this.parameters = parameters;
        this.updateNotificationCount();
      });

    this.loadDependentsList();

    HttpService.get(APPLICATION_ENDPOINTS.TASY_RESCHEDULE_PARAMETERS).then(res => {
      RescheduleContext.rescheduleParameters = res.body as TasyRescheduleParameters;
    });

    HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_SCHEDULE_CONFIG).then(res => {
      RescheduleContext.scheduleParameters = res.body as ScheduleParameters;
    });
  }

  @OnUnmount(PatientRootAliases.FETCH_USER_PROFILE_HOLDER)
  handlePageExit(): void {
    this.fetchDropdownUserPrincipal.clearPersistedState();
  }

  /**
   * holderPatientId - It refers to the account holder. It must not be changed while using the application.
   * patientId - Refers to the selected profile. This profile can be changed in the dropdown in the applicationBar(header).
   * Note: Both initial with the same value.
   */
  private setUserContextData(patientId: number): void {
    UserContext.patientId = patientId;
    UserContext.holderPatientId = patientId;

    HttpService.get<UserPrincipal>(APPLICATION_ENDPOINTS.GET_PHYSICAL_PERSON_DATA_WITH_PHOTO).then(response => {
      UserContext.userPrincipal = response.body;
    });

    HttpService.get<UserDependent[]>(APPLICATION_ENDPOINTS.PROFILE_DEPENDENTS_LIST).then(response => {
      UserContext.userDependents = response.body;
    });
  }

  @OnMount({ target: 'mapper-user-profile' })
  handleMountMapperUserProfile(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    this.mapperUserProfile = mapperApi;
    this.updatePatientDataInAvatarByMapper(mapperApi);
  }

  @OnClick({ target: 'application-bar' })
  handleApplicationBarAvatarClick(): void {
    const modalProps: BaseModalProps = {
      closeable: true,
      fragment: 'change-user-modal',
      onSubmit: this.handleSubmitChangeUserModal.bind(this),
    };

    ModalService.openFullscreen(modalProps);
  }

  private handleSubmitChangeUserModal(event: SubmitEvent): void {
    const { patientId } = event.record;
    this.switchProfile(patientId);
  }

  @OnMount({target: 'fetch-notification'})
  handleMountFetchNot(): void {
    this.fetchNotification.setParameters({ limit: 15, page: 0 })
  }

  @OnOpen({ target: 'drop-down-menu-user-profile' })
  onOpenDropDownMenuUserProfile(): void {
    this.updatePatientDataInAvatarByMapper(this.mapperUserDependents);
    this.showDependents();
  }

  @OnBeforeFetch({ target: PatientRootAliases.FETCH_USER_PROFILE_HOLDER })
  handleBeforeFetchUserProfile(event: AbortableFetchEvent): void {
    if (this.fetchDropdownUserPrincipal.restoreState()) {
      event.abort();
    }
  }

  @OnAfterFetch({ target: PatientRootAliases.FETCH_USER_PROFILE_HOLDER })
  handleAfterFetchUserProfile(): void {
    this.fetchDropdownUserPrincipal.persistState();
  }

  @OnClick({ target: PatientRootAliases.CARD_CONTAINER_HOLDER })
  onClickCardMainPatient(): void {
    this.switchProfile(this.holderPatientId);
    this.fechUserProfile.forceFetch();
    this.updatePatientDataInAvatarByMapper(this.mapperUserDependents);
  }

  /*
  Create the list of dependents' cards. This process
  would normally be done using a fetch component that
  would load the data onto the deck. But in this case,
  the data was passed manually to the deck, due to
  some mapping problem in the componente Mapper.
  */
  @OnMount({ target: 'deck-dependents' })
  onMountDeckDependents(mountEvent: MountEvent): void {
    const deckDependents = mountEvent.target as DeckAPI;

    const mappedDependentList = this.dependentList.map(dependent => {
      const manualMapping = {
        image: dependent.imPessoaFisicaBase64 ? dependent.imPessoaFisicaBase64 : '/static/assets/icons/avatar-user-gray.svg',
        subtitle: dependent.dsGrauParentesco,
        title: dependent.nmPessoaDependente,
      };
      const dependentMapping = Object.assign(dependent, manualMapping) as PessoaFisicaDependente;
      const workAroundMapperNotWorking = { ...dependentMapping }
      return workAroundMapperNotWorking;
    });

    deckDependents.setData(mappedDependentList);
    deckDependents.setDone(true);
  }

  @OnClick({ target: 'deck-dependents' })
  onClickDeckDependents(clickEvent: ClickEvent): void {
    const record = clickEvent.value as Record<string, string>;

    if (!record.cdPessoaDependente) {
      return;
    }

    this.switchProfile(Number(record.cdPessoaDependente));
  }

  @OnClick(PatientRootAliases.OPTION_PROFILE)
  onClickAvatar(): void {
    applicationContext.openModule(ApplicationModulesIds.PROFILE);
  }

  @OnClick(PatientRootAliases.OPTION_LOGOUT)
  OnLogoutLinkClick(): void {
    AuthenticationService.logout().then(() => {
      AuthenticationService.redirectToLogin();
    });
  }

  @OnAfterFetch({ target: 'fetch-notification' })
  onAfterFetchFetchNotification(fetchEvent: FetchEvent): void {
    const notifications = fetchEvent.data as Notification[];
    if (!notifications) {
      return;
    }

    const notificationFormattedData = notifications.map((data, index) => {
      const isNewPage = index >= (notifications.length - 15);

      if (!isNewPage) {
        return data;
      }

      const isRead = data.notificationStatus === 'R';
      const notificationDate = moment(new Date(data.dtAtualizacaoNrec)).utc().format('YYYY-MM-DD HH:mm:ss');

      const dataNot = {
        ...data,
        date: notificationDate,
        description: data.resultDescription,
        id: data.nrSequencia,
        read: isRead,
        title: data.examName || ' - ',
        type: Type.NOTICE,
      }

      return dataNot;
    })

    this.notificationCenter.setData(cast(notificationFormattedData))
  }

  @OnMount({ target: ['text-component'] })
  async onMountApplicationName(mountEvent: MountEvent): Promise<void> {
    const applicationName = mountEvent.target as LinkAPI;
    await ConfigurationService.getConfigurationData().then(layoutData => applicationName.setValue(layoutData.nmInstituicao));
  }

  @OnOpen({ target: 'notification-center' })
  onOpenNotification(): void {
    if (this.areNotificationsAlreadySetToViewed) {
      return;
    }

    this.updateAllNotificationsToViewed();
  }

  @OnClick({ target: 'notification-center' })
  async onNotificationClick(event: ClickEvent): Promise<void> {
    switch (event.value.type) {
      case NOTIFICATION_CLICK_TYPE.MARK_ALL_AS_READ:
        this.updateAllNotificationsToRead();
        break;
      case NOTIFICATION_CLICK_TYPE.MARK_AS_READ:
      case NOTIFICATION_CLICK_TYPE.MARK_AS_UNREAD:
        await this.handleMarkAsReadOrUnreadClick(event);
        break;
      case NOTIFICATION_CLICK_TYPE.NOTIFICATION:
        await this.handleNotificationClick(event);
        break;
      case NOTIFICATION_CLICK_TYPE.DELETE:
        this.handleDeleteNotificationClick(event);
        break;
    }
  }

  private async handleNotificationClick(event: ClickEvent): Promise<void> {
    const { id, read } = event.value.item;
    const fetchData: Notification[] = cast(this.fetchNotification.getData());
    const notificationData = fetchData.find((data) => data.nrSequencia === id);
    if (!notificationData) {
      return;
    }

    const navigationUrlAndParameters = this.getRouteDataForNavigation(notificationData);

    if (navigationUrlAndParameters == null) {
      return;
    }

    const parameters = { ...navigationUrlAndParameters?.parameters } as ParametersItem;

    if (read) {
      applicationContext.popNavigationItem();
      this.pushToUrl(navigationUrlAndParameters, parameters);
      return;
    }

    await this.updateNotificationStatus(id, read);
    applicationContext.popNavigationItem();
    this.pushToUrl(navigationUrlAndParameters, parameters);
  }

  doRouteValidations(): void {
    this.validateAvailableModules();
    this.validateCurrentRoute();
    this.validateAppointmentRoute();
  }

  validateCurrentRoute(): void {
    const currentRoute = this.historyService.getCurrentRoute();
    const availableModules = UserContext.twsUserAvailableModules;
    if(availableModules) {
      let isValidRoute = false;
      availableModules.forEach(availableModule => {
        const module = Array.from(ApplicationModules.values()).find(e => e.id === availableModule.cdFunction);
        if(module && currentRoute.startsWith(module.route)) {
          isValidRoute = true;
        }
      });
      if(!isValidRoute) {
        applicationContext.openModule(ApplicationModulesIds.DASHBOARD);
      }

    }
  }

  validateAvailableModules(): void {
    const availableModules = UserContext.twsUserAvailableModules;

    if(availableModules && availableModules.length > 0) {
      availableModules.forEach(availableModule => {
        const module = Array.from(ApplicationModules.values()).find(e => e.id === availableModule.cdFunction);
        if(module && module.id === ApplicationModulesIds.DASHBOARD) {
          applicationContext.openModule(module.id, {preventNavigation: module.preventNavigation});
          return;
        }
      });
    }
  }

  async validateAppointmentRoute(): Promise<void> {

    const currentRoute = this.historyService.getCurrentRoute();
    if (currentRoute === APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS) {
      applicationContext.redirect(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS, { patientId: this.patientId });
    }

    const appointmentModule = applicationModuleRegistry.get(ApplicationModulesIds.APPOINTMENTS);
    appointmentModule.route = APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS;
  }

  @OnMount({ target: PatientRootAliases.TELECONSULTATION })
  onMountTeleconsulation(evt: MountEvent): void {
    const teleconsultattionApi = evt.target as TeleconsultattionApi;
    BrokerService.subscribeToTopic(FragmentNames.IFRAME_TELECONSULTA, (obj: UrlTelemedicine) => {
      teleconsultattionApi.setCallbackUrl(obj.patientUrl);
    });
  }

  private notifyChangedUserToTaskComponent(patientId: number): void {
    BrokerService.publishToTopic(BS_CHANGE_USER, patientId);
  }
}
